import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  PenSquare, Star, CheckCircle, Bold, Italic, Hash, AtSign,
  Eye, Shuffle, ThumbsUp, Copy, X, AlertCircle, Image as ImageIcon, Video, Paperclip,
} from 'lucide-react'
import { Card, Badge, PageHeader, EmptyState } from '../components/Card'
import {
  listDrafts, listAssets, createDraft, reviewDraft, scoreDraft,
  approveDraft, draftVariants, replaceDraftAssets,
  type Draft, type MediaAsset,
} from '../api/client'
import { layout } from '../utils/layout'

const PLATFORMS = ['facebook', 'instagram', 'linkedin', 'twitter']

const CHAR_LIMITS: Record<string, number> = {
  twitter: 280,
  facebook: 63206,
  instagram: 2200,
  linkedin: 3000,
}

const STATUS_COLOR: Record<string, 'slate' | 'yellow' | 'green' | 'blue'> = {
  draft: 'slate',
  reviewed: 'yellow',
  approved: 'green',
  published: 'blue',
}

// ── Platform Preview ───────────────────────────────────────────────────────────

function PlatformPreview({ platform, content }: { platform: string; content: string }) {
  const limit = CHAR_LIMITS[platform] ?? 9999
  const truncated = content.length > limit ? content.slice(0, limit) + '…' : content
  const over = content.length > limit

  if (platform === 'instagram') {
    return (
      <div className="bg-white rounded-xl overflow-hidden border border-gray-200 text-sm">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white text-[10px] font-bold">YB</div>
          <div>
            <div className="text-xs font-semibold text-gray-900">your_brand</div>
            <div className="text-[10px] text-gray-500">Sponsored</div>
          </div>
          <span className="ml-auto text-gray-400 text-xs">•••</span>
        </div>
        <div className="bg-gray-100 h-28 flex items-center justify-center">
          <span className="text-gray-400 text-xs">Image / Video</span>
        </div>
        <div className="px-3 py-2">
          <div className="text-[11px] text-gray-900 leading-relaxed">
            <span className="font-semibold">your_brand</span>{' '}{truncated}
          </div>
          {over && <p className="text-[10px] text-red-500 mt-1">Exceeds {limit.toLocaleString()} char limit</p>}
        </div>
        <div className="px-3 pb-2 flex gap-3 text-gray-400 text-xs">
          <span>❤️ Like</span><span>💬 Comment</span><span>✈️ Share</span>
        </div>
      </div>
    )
  }

  if (platform === 'facebook') {
    return (
      <div className="bg-white rounded-md overflow-hidden border border-gray-200 text-sm">
        <div className="flex items-center gap-2 px-3 pt-3 pb-2">
          <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white text-[10px] font-bold">YB</div>
          <div>
            <div className="text-xs font-semibold text-gray-900">Your Brand</div>
            <div className="text-[10px] text-gray-500">Just now · Public</div>
          </div>
        </div>
        <p className="px-3 text-xs text-gray-800 leading-relaxed pb-3">{truncated}</p>
        {over && <p className="text-[10px] text-red-500 px-3 pb-2">Exceeds {limit.toLocaleString()} char limit</p>}
        <div className="border-t border-gray-100 px-3 py-2 flex justify-around text-[10px] text-gray-500">
          <span>Like</span><span>Comment</span><span>Share</span>
        </div>
      </div>
    )
  }

  if (platform === 'twitter') {
    return (
      <div className="bg-[#0f0f0f] rounded-xl overflow-hidden border border-gray-800 text-sm">
        <div className="flex gap-2.5 px-3 pt-3 pb-2">
          <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center text-white text-[10px] font-bold shrink-0">YB</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-1">
              <span className="text-xs font-bold text-white">Your Brand</span>
              <span className="text-[10px] text-gray-500">@yourbrand · now</span>
            </div>
            <p className="text-xs mt-1 text-white leading-relaxed">{truncated}</p>
            {over && <p className="text-[10px] text-red-400 mt-1">Exceeds {limit} char limit</p>}
            <div className="flex gap-4 mt-2 text-[10px] text-gray-500">
              <span>💬 0</span><span>🔁 0</span><span>❤️ 0</span><span>📊 Views</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (platform === 'linkedin') {
    return (
      <div className="bg-white rounded-xl overflow-hidden border border-gray-200 text-sm">
        <div className="flex items-center gap-2 px-3 pt-3 pb-2">
          <div className="w-9 h-9 rounded bg-blue-600 flex items-center justify-center text-white text-[10px] font-bold">YB</div>
          <div>
            <div className="text-xs font-semibold text-gray-900">Your Brand</div>
            <div className="text-[10px] text-gray-500">Company · 1,234 followers</div>
            <div className="text-[10px] text-gray-400">1h · 🌍</div>
          </div>
        </div>
        <p className="px-3 text-xs text-gray-800 leading-relaxed pb-2">{truncated}</p>
        {over && <p className="text-[10px] text-red-500 px-3 pb-2">Exceeds {limit.toLocaleString()} char limit</p>}
        <div className="border-t border-gray-100 px-3 py-1.5 flex gap-4 text-[10px] text-gray-500">
          <span>👍 Like</span><span>💬 Comment</span><span>↗ Share</span><span>✉️ Send</span>
        </div>
      </div>
    )
  }

  // TikTok
  return (
    <div className="bg-black rounded-xl overflow-hidden border border-gray-800 text-sm">
      <div className="h-28 bg-gradient-to-b from-gray-800 to-gray-900 flex items-center justify-center relative">
        <span className="text-gray-500 text-xs">Video</span>
        <div className="absolute right-2 bottom-2 flex flex-col gap-2 items-center">
          <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center text-white text-[9px]">YB</div>
          <span className="text-[9px] text-white">❤️ 0</span>
          <span className="text-[9px] text-white">💬 0</span>
        </div>
      </div>
      <div className="px-3 py-2">
        <span className="text-[10px] font-semibold text-white">@yourbrand </span>
        <span className="text-[10px] text-white">{truncated}</span>
        {over && <p className="text-[10px] text-red-400 mt-1">Exceeds {limit} char limit</p>}
      </div>
    </div>
  )
}

// ── Character Count Bar ────────────────────────────────────────────────────────

function CharBar({ content, platforms }: { content: string; platforms: string[] }) {
  if (!platforms.length || !content) return null
  return (
    <div className="space-y-1.5">
      {platforms.map(p => {
        const limit = CHAR_LIMITS[p] ?? 9999
        const len = content.length
        const pct = Math.min((len / limit) * 100, 100)
        const over = len > limit
        return (
          <div key={p}>
            <div className="flex justify-between text-[10px] mb-0.5">
              <span className="text-slate-500 capitalize">{p}</span>
              <span className={over ? 'text-red-400 font-semibold' : len > limit * 0.9 ? 'text-yellow-400' : 'text-slate-500'}>
                {len.toLocaleString()} / {limit.toLocaleString()}
              </span>
            </div>
            <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${over ? 'bg-red-500' : len > limit * 0.9 ? 'bg-yellow-500' : 'bg-brand-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Main ContentView ───────────────────────────────────────────────────────────

export default function ContentView() {
  const qc = useQueryClient()
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const [showCreate, setShowCreate] = useState(false)
  const [showEditAssets, setShowEditAssets] = useState(false)
  const [editAssetIds, setEditAssetIds] = useState<string[]>([])
  const [selected, setSelected] = useState<Draft | null>(null)
  const [previewPlatform, setPreviewPlatform] = useState('instagram')
  const [showPreview, setShowPreview] = useState(false)
  const [form, setForm] = useState({
    content: '',
    platforms: ['facebook'] as string[],
    assetIds: [] as string[],
  })
  const [scoreData, setScoreData] = useState<{
    score: number
    breakdown: { factor: string; impact: number; description: string }[]
    suggestions: string[]
  } | null>(null)
  const [variants, setVariants] = useState<{ id: string; content: string }[] | null>(null)

  const { data } = useQuery({
    queryKey: ['drafts'],
    queryFn: () => listDrafts(undefined, 30),
  })

  const { data: assetData, isLoading: assetsLoading } = useQuery({
    queryKey: ['assets'],
    queryFn: () => listAssets(50),
    enabled: showCreate || showEditAssets,
  })

  const allAssets = assetData?.assets ?? []
  const publishableAssets = allAssets.filter(asset =>
    ['generated_internal', 'owned', 'licensed', 'cleared'].includes(asset.rights_status.toLowerCase()),
  )
  const selectedAssets = publishableAssets.filter(asset => form.assetIds.includes(asset.id))
  const selectedAssetBrand = selectedAssets[0]?.brand_id
  const mediaPlatformConflict = form.assetIds.length > 0 && form.platforms.some(platform => platform === 'twitter')
  const instagramMissingMedia = form.platforms.includes('instagram') && form.assetIds.length === 0
  const maxSelectedAssets = form.platforms.every(platform => platform === 'instagram') ? 10 : 1
  const editableAssets = selected?.brand_id
    ? allAssets.filter(asset => asset.brand_id === selected.brand_id)
    : []
  const editMaxSelectedAssets = selected?.platforms.every(platform => platform === 'instagram') ? 10 : 1
  const editMediaPlatformConflict = editAssetIds.length > 0
    && Boolean(selected?.platforms.some(platform => platform === 'twitter'))
  const editInstagramMissingMedia = Boolean(
    selected?.platforms.includes('instagram') && editAssetIds.length === 0,
  )
  const editHasUnavailableSelection = editAssetIds.some(assetId => {
    const asset = editableAssets.find(item => item.id === assetId)
    return !asset || !['generated_internal', 'owned', 'licensed', 'cleared'].includes(asset.rights_status.toLowerCase())
  })

  const createMut = useMutation({
    mutationFn: () => createDraft({
      content: form.content,
      platforms: form.platforms,
      brand_id: selectedAssetBrand,
      assets: selectedAssets.map(asset => ({ asset_id: asset.id })),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['drafts'] })
      setShowCreate(false)
      setForm({ content: '', platforms: ['facebook'], assetIds: [] })
    },
  })

  const reviewMut = useMutation({
    mutationFn: (id: string) => reviewDraft(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drafts'] }),
  })

  const scoreMut = useMutation({
    mutationFn: (id: string) => scoreDraft(id),
    onSuccess: (d) => setScoreData(d),
  })

  const approveMut = useMutation({
    mutationFn: (id: string) => approveDraft(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drafts'] }),
  })

  const variantsMut = useMutation({
    mutationFn: (id: string) => draftVariants(id),
    onSuccess: (d) => setVariants(d.variants),
  })

  const replaceAssetsMut = useMutation({
    mutationFn: () => {
      if (!selected) throw new Error('No draft selected')
      return replaceDraftAssets(
        selected.id,
        editAssetIds.map(assetId => {
          const current = selected.assets.find(asset => asset.asset_id === assetId)
          return {
            asset_id: assetId,
            ...(current?.alt_text ? { alt_text: current.alt_text } : {}),
          }
        }),
      )
    },
    onSuccess: (updated) => {
      setSelected(current => current?.id === updated.draft_id
        ? {
            ...current,
            brand_id: updated.brand_id,
            status: updated.status,
            assets: updated.assets,
          }
        : current)
      qc.invalidateQueries({ queryKey: ['drafts'] })
      setShowEditAssets(false)
    },
  })

  function insertFormat(before: string, after = '') {
    const el = textareaRef.current
    if (!el) return
    const s = el.selectionStart ?? 0
    const e = el.selectionEnd ?? 0
    const sel = el.value.slice(s, e) || 'text'
    const newVal = el.value.slice(0, s) + before + sel + after + el.value.slice(e)
    setForm(f => ({ ...f, content: newVal }))
    setTimeout(() => {
      el.focus()
      el.setSelectionRange(s + before.length, s + before.length + sel.length)
    }, 0)
  }

  const togglePlatform = (p: string) => {
    setForm(f => ({
      ...f,
      platforms: f.platforms.includes(p)
        ? f.platforms.filter(x => x !== p)
        : [...f.platforms, p],
    }))
  }

  const toggleAsset = (asset: MediaAsset) => {
    setForm(current => {
      if (current.assetIds.includes(asset.id)) {
        return { ...current, assetIds: current.assetIds.filter(id => id !== asset.id) }
      }
      const currentAssets = publishableAssets.filter(item => current.assetIds.includes(item.id))
      if (currentAssets.some(item => item.brand_id !== asset.brand_id)) return current
      if (current.assetIds.length >= maxSelectedAssets) return current
      return { ...current, assetIds: [...current.assetIds, asset.id] }
    })
  }

  const openAssetEditor = () => {
    if (!selected?.brand_id || selected.status === 'published') return
    replaceAssetsMut.reset()
    setEditAssetIds(selected.assets.map(asset => asset.asset_id))
    setShowEditAssets(true)
  }

  const toggleEditAsset = (asset: MediaAsset) => {
    setEditAssetIds(current => {
      if (current.includes(asset.id)) return current.filter(id => id !== asset.id)
      if (current.length >= editMaxSelectedAssets) return current
      return [...current, asset.id]
    })
  }

  const complianceLevel = scoreData
    ? scoreData.score >= 70 ? 'good' : scoreData.score >= 50 ? 'warning' : 'poor'
    : null

  return (
    <div>
      <PageHeader
        title="Content Studio"
        subtitle="Draft → Review → Score → Approve · Platform previews · AI variants"
        action={
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm px-3 py-1.5 rounded-lg transition-colors"
          >
            <PenSquare className="w-4 h-4" /> New Draft
          </button>
        }
      />

      <div className={`${layout.pagePadding} ${layout.pageStack}`}>
        <div className="flex gap-4">
          {/* Draft list */}
          <div className="flex-1 min-w-0">
            <Card title={`${data?.total ?? 0} Drafts`}>
              {!data?.drafts.length ? (
                <EmptyState
                  icon={<PenSquare className="w-12 h-12" />}
                  title="No drafts yet"
                  description="Write your first post. SocialScale will review it for tone, compliance, and brand fit."
                  action={
                    <button
                      onClick={() => setShowCreate(true)}
                      className="bg-brand-600 hover:bg-brand-700 text-white text-sm px-4 py-2 rounded-lg"
                    >
                      New Draft
                    </button>
                  }
                />
              ) : (
                <div className="divide-y divide-surface-border">
                  {data.drafts.map((d: Draft) => (
                    <div
                      key={d.id}
                      onClick={() => { setSelected(d); setShowEditAssets(false); setScoreData(null); setVariants(null) }}
                      className={`py-3 px-2 cursor-pointer hover:bg-slate-800/40 rounded-lg transition-colors ${selected?.id === d.id ? 'bg-slate-800/60' : ''}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge color={STATUS_COLOR[d.status] ?? 'slate'}>{d.status}</Badge>
                            <span className="text-xs text-slate-500">{d.platforms.join(', ')}</span>
                            {(d.assets?.length ?? 0) > 0 && (
                              <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                                <Paperclip className="w-3 h-3" /> {d.assets.length}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-300 line-clamp-2">{d.content}</p>
                          <p className="text-xs text-slate-600 mt-1">{new Date(d.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Detail panel */}
          {selected && (
            <div className="w-80 shrink-0 space-y-3">
              {/* Platform Preview */}
              <Card title="Platform Preview">
                <div className="space-y-2">
                  <div className="flex gap-1 flex-wrap">
                    {selected.platforms.map(p => (
                      <button
                        key={p}
                        onClick={() => setPreviewPlatform(p)}
                        className={`text-[10px] px-2.5 py-1 rounded-full capitalize transition-colors ${
                          previewPlatform === p
                            ? 'bg-brand-700 text-brand-100'
                            : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                  <PlatformPreview platform={previewPlatform} content={selected.content} />
                  <CharBar content={selected.content} platforms={selected.platforms} />
                  {(selected.assets?.length ?? 0) > 0 && (
                    <div className="border-t border-surface-border pt-2 space-y-1">
                      {selected.assets.map(asset => (
                        <div key={asset.id} className="flex items-center gap-2 text-xs text-slate-400">
                          {asset.asset_type === 'video'
                            ? <Video className="w-3.5 h-3.5" />
                            : <ImageIcon className="w-3.5 h-3.5" />}
                          <span className="capitalize">{asset.asset_type}</span>
                          <span className="ml-auto font-mono text-[10px] text-slate-600">
                            {asset.asset_id.slice(0, 8)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>

              {/* Actions + Score */}
              <Card title="Draft Actions">
                <div className="space-y-3">
                  {/* Brand compliance indicator */}
                  {complianceLevel && (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
                      complianceLevel === 'good'
                        ? 'bg-green-900/20 border border-green-900/30 text-green-400'
                        : complianceLevel === 'warning'
                        ? 'bg-yellow-900/20 border border-yellow-900/30 text-yellow-400'
                        : 'bg-red-900/20 border border-red-900/30 text-red-400'
                    }`}>
                      {complianceLevel === 'good'
                        ? <CheckCircle className="w-3.5 h-3.5" />
                        : <AlertCircle className="w-3.5 h-3.5" />}
                      <span>Brand compliance:</span>
                      <span className="font-semibold ml-0.5">
                        {complianceLevel === 'good' ? 'Good' : complianceLevel === 'warning' ? 'Needs review' : 'Poor fit'}
                      </span>
                      <span className="ml-auto font-bold">{scoreData?.score}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => reviewMut.mutate(selected.id)}
                      disabled={reviewMut.isPending}
                      className="flex items-center justify-center gap-1 bg-slate-700 hover:bg-slate-600 text-xs py-1.5 rounded-lg transition-colors"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      {reviewMut.isPending ? 'Reviewing…' : 'AI Review'}
                    </button>
                    <button
                      onClick={() => scoreMut.mutate(selected.id)}
                      disabled={scoreMut.isPending}
                      className="flex items-center justify-center gap-1 bg-brand-600 hover:bg-brand-700 text-white text-xs py-1.5 rounded-lg transition-colors"
                    >
                      <Star className="w-3.5 h-3.5" />
                      {scoreMut.isPending ? 'Scoring…' : 'Score'}
                    </button>
                    <button
                      onClick={() => variantsMut.mutate(selected.id)}
                      disabled={variantsMut.isPending}
                      className="flex items-center justify-center gap-1 bg-slate-700 hover:bg-slate-600 text-xs py-1.5 rounded-lg transition-colors"
                    >
                      <Shuffle className="w-3.5 h-3.5" />
                      {variantsMut.isPending ? 'Generating…' : 'Variants'}
                    </button>
                    <button
                      onClick={() => approveMut.mutate(selected.id)}
                      disabled={approveMut.isPending || selected.status === 'approved'}
                      className="flex items-center justify-center gap-1 bg-green-700/40 hover:bg-green-700/60 text-green-300 disabled:opacity-40 text-xs py-1.5 rounded-lg transition-colors"
                    >
                      <ThumbsUp className="w-3.5 h-3.5" />
                      {selected.status === 'approved' ? 'Approved' : 'Approve'}
                    </button>
                    <button
                      onClick={openAssetEditor}
                      disabled={selected.status === 'published' || !selected.brand_id}
                      title={selected.status === 'published' ? 'Published draft media is immutable' : 'Edit owned media'}
                      className="col-span-2 flex items-center justify-center gap-1 bg-slate-700 hover:bg-slate-600 disabled:opacity-40 text-xs py-1.5 rounded-lg transition-colors"
                    >
                      <Paperclip className="w-3.5 h-3.5" />
                      Edit media
                    </button>
                  </div>

                  {/* Score breakdown */}
                  {scoreData && (
                    <div className="space-y-2 pt-2 border-t border-surface-border">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500">Content Score</span>
                        <span className={`text-xl font-bold ${scoreData.score >= 70 ? 'text-green-400' : scoreData.score >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {scoreData.score}
                        </span>
                      </div>
                      {scoreData.breakdown.map((b, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <span className="text-slate-400">{b.factor}</span>
                          <span className={b.impact >= 0 ? 'text-green-400' : 'text-red-400'}>
                            {b.impact >= 0 ? '+' : ''}{b.impact}
                          </span>
                        </div>
                      ))}
                      {scoreData.suggestions.length > 0 && (
                        <div className="pt-1 space-y-1">
                          <p className="text-xs text-slate-500">Suggestions:</p>
                          {scoreData.suggestions.map((s, i) => (
                            <p key={i} className="text-xs text-slate-400 bg-slate-800 rounded p-1.5">{s}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Variants */}
                  {variants && variants.length > 0 && (
                    <div className="space-y-2 pt-2 border-t border-surface-border">
                      <p className="text-xs text-slate-500">{variants.length} AI Variants</p>
                      {variants.map((v, i) => (
                        <div key={v.id} className="bg-slate-800 rounded-lg p-2 text-xs text-slate-300 relative group">
                          <span className="text-slate-600 mr-1">V{i + 1}:</span>
                          {v.content.slice(0, 100)}{v.content.length > 100 ? '…' : ''}
                          <button
                            onClick={() => navigator.clipboard.writeText(v.content)}
                            className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Copy"
                          >
                            <Copy className="w-3 h-3 text-slate-400 hover:text-slate-200" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Create Draft Modal */}
      {showCreate && (
        <div
          className="fixed inset-0 bg-black/60 flex items-start justify-center z-50 pt-12 px-4 overflow-y-auto"
          onClick={() => setShowCreate(false)}
        >
          <div
            className="bg-surface-card border border-surface-border rounded-2xl w-full max-w-2xl mb-8"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-surface-border">
              <h2 className="text-lg font-semibold">New Draft</h2>
              <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-slate-200">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Platform toggles */}
              <div>
                <label className="text-xs text-slate-400 mb-2 block">Platforms</label>
                <div className="flex flex-wrap gap-2">
                  {PLATFORMS.map(p => (
                    <button
                      key={p}
                      onClick={() => togglePlatform(p)}
                      className={`text-xs px-3 py-1 rounded-full capitalize transition-colors ${
                        form.platforms.includes(p)
                          ? 'bg-brand-700 text-brand-100'
                          : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Formatting toolbar + textarea */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Content</label>
                <div className="flex items-center gap-1 px-2 py-1.5 bg-slate-800 border border-surface-border rounded-t-lg border-b-0">
                  <button
                    onClick={() => insertFormat('**', '**')}
                    className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-slate-200 transition-colors"
                    title="Bold"
                  >
                    <Bold className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => insertFormat('_', '_')}
                    className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-slate-200 transition-colors"
                    title="Italic"
                  >
                    <Italic className="w-3.5 h-3.5" />
                  </button>
                  <div className="w-px h-4 bg-slate-700 mx-0.5" />
                  <button
                    onClick={() => insertFormat('#')}
                    className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-slate-200 transition-colors"
                    title="Hashtag"
                  >
                    <Hash className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => insertFormat('@')}
                    className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-slate-200 transition-colors"
                    title="Mention"
                  >
                    <AtSign className="w-3.5 h-3.5" />
                  </button>
                  <div className="w-px h-4 bg-slate-700 mx-0.5" />
                  <button
                    onClick={() => setShowPreview(v => !v)}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${showPreview ? 'text-brand-300 bg-brand-900/30' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'}`}
                  >
                    <Eye className="w-3.5 h-3.5" /> Preview
                  </button>
                </div>
                <textarea
                  ref={textareaRef}
                  rows={5}
                  value={form.content}
                  onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                  placeholder="Write your post here…"
                  className="w-full bg-slate-800 border border-surface-border rounded-b-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-brand-500"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-slate-400">Owned media</label>
                  <span className="text-[10px] text-slate-500 tabular-nums">
                    {form.assetIds.length} / {maxSelectedAssets}
                  </span>
                </div>
                <div className="border border-surface-border rounded-md max-h-44 overflow-y-auto divide-y divide-surface-border">
                  {assetsLoading ? (
                    <div className="px-3 py-4 text-xs text-slate-500">Loading media…</div>
                  ) : publishableAssets.length === 0 ? (
                    <div className="px-3 py-4 text-xs text-slate-500">No publishable owned media</div>
                  ) : publishableAssets.map(asset => {
                    const checked = form.assetIds.includes(asset.id)
                    const brandMismatch = Boolean(selectedAssetBrand && selectedAssetBrand !== asset.brand_id)
                    const limitReached = form.assetIds.length >= maxSelectedAssets
                    const disabled = !checked && (brandMismatch || limitReached || form.platforms.includes('twitter'))
                    return (
                      <label
                        key={asset.id}
                        className={`flex items-center gap-3 px-3 py-2 ${disabled ? 'opacity-40' : 'hover:bg-slate-800/50 cursor-pointer'}`}
                        title={disabled ? 'Not compatible with the current brand or platform selection' : undefined}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggleAsset(asset)}
                          className="w-4 h-4 accent-brand-600"
                        />
                        {asset.asset_type === 'video'
                          ? <Video className="w-4 h-4 text-sky-400 shrink-0" />
                          : <ImageIcon className="w-4 h-4 text-emerald-400 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-xs text-slate-300">
                            {asset.storage_key.split('/').pop() ?? asset.id}
                          </div>
                          <div className="text-[10px] text-slate-500">
                            {asset.content_type} · {(asset.size_bytes / 1024).toFixed(1)} KB · {asset.rights_status}
                          </div>
                        </div>
                      </label>
                    )
                  })}
                </div>
              </div>

              {/* Character count bars */}
              {form.content && <CharBar content={form.content} platforms={form.platforms} />}

              {/* Live preview */}
              {showPreview && form.content && form.platforms.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-2 capitalize">Preview — {form.platforms[0]}</p>
                  <PlatformPreview platform={form.platforms[0]} content={form.content} />
                </div>
              )}

              {createMut.isError && (
                <div className="flex items-start gap-2 text-xs text-red-300 bg-red-950/30 border border-red-900/40 rounded-md px-3 py-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{createMut.error instanceof Error ? createMut.error.message : 'Draft creation failed'}</span>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowCreate(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-sm py-2 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() => createMut.mutate()}
                  disabled={
                    createMut.isPending
                    || !form.content.trim()
                    || !form.platforms.length
                    || mediaPlatformConflict
                    || instagramMissingMedia
                  }
                  title={
                    mediaPlatformConflict
                      ? 'X drafts are text-only in the current publisher runtime'
                      : instagramMissingMedia
                        ? 'Instagram drafts require owned media'
                        : undefined
                  }
                  className="flex-1 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm py-2 rounded-lg"
                >
                  {createMut.isPending ? 'Creating…' : 'Create Draft'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditAssets && selected && (
        <div
          className="fixed inset-0 bg-black/60 flex items-start justify-center z-50 pt-12 px-4 overflow-y-auto"
          onClick={() => setShowEditAssets(false)}
        >
          <div
            className="bg-surface-card border border-surface-border rounded-md w-full max-w-xl mb-8"
            onClick={event => event.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border">
              <div>
                <h2 className="text-base font-semibold">Edit media</h2>
                <p className="text-xs text-slate-500 mt-0.5">{selected.platforms.join(', ')}</p>
              </div>
              <button
                onClick={() => setShowEditAssets(false)}
                className="p-1 text-slate-400 hover:text-slate-200"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">Owned media</span>
                <span className="text-[10px] text-slate-500 tabular-nums">
                  {editAssetIds.length} / {editMaxSelectedAssets}
                </span>
              </div>
              <div className="border border-surface-border rounded-md max-h-64 overflow-y-auto divide-y divide-surface-border">
                {assetsLoading ? (
                  <div className="px-3 py-5 text-xs text-slate-500">Loading media…</div>
                ) : editableAssets.length === 0 ? (
                  <div className="px-3 py-5 text-xs text-slate-500">No owned media for this brand</div>
                ) : editableAssets.map(asset => {
                  const checked = editAssetIds.includes(asset.id)
                  const publishable = ['generated_internal', 'owned', 'licensed', 'cleared']
                    .includes(asset.rights_status.toLowerCase())
                  const limitReached = editAssetIds.length >= editMaxSelectedAssets
                  const disabled = !checked && (!publishable || limitReached || editMediaPlatformConflict)
                  return (
                    <label
                      key={asset.id}
                      className={`flex items-center gap-3 px-3 py-2.5 ${disabled ? 'opacity-40' : 'hover:bg-slate-800/50 cursor-pointer'}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleEditAsset(asset)}
                        className="w-4 h-4 accent-brand-600"
                      />
                      {asset.asset_type === 'video'
                        ? <Video className="w-4 h-4 text-sky-400 shrink-0" />
                        : <ImageIcon className="w-4 h-4 text-emerald-400 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs text-slate-300">
                          {asset.storage_key.split('/').pop() ?? asset.id}
                        </div>
                        <div className="text-[10px] text-slate-500">
                          {asset.content_type} · {(asset.size_bytes / 1024).toFixed(1)} KB · {asset.rights_status}
                        </div>
                      </div>
                    </label>
                  )
                })}
              </div>

              {selected.status !== 'draft' && (
                <div className="flex items-start gap-2 text-xs text-yellow-300 bg-yellow-950/20 border border-yellow-900/40 rounded-md px-3 py-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Changing the attachment set returns this draft to review and cancels pending schedules.</span>
                </div>
              )}
              {replaceAssetsMut.isError && (
                <div className="flex items-start gap-2 text-xs text-red-300 bg-red-950/30 border border-red-900/40 rounded-md px-3 py-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{replaceAssetsMut.error instanceof Error ? replaceAssetsMut.error.message : 'Media update failed'}</span>
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setShowEditAssets(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-sm py-2 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={() => replaceAssetsMut.mutate()}
                  disabled={
                    replaceAssetsMut.isPending
                    || editMediaPlatformConflict
                    || editInstagramMissingMedia
                    || editHasUnavailableSelection
                  }
                  title={
                    editMediaPlatformConflict
                      ? 'X drafts are text-only in the current publisher runtime'
                      : editInstagramMissingMedia
                        ? 'Instagram drafts require owned media'
                        : editHasUnavailableSelection
                          ? 'Remove unavailable media before saving'
                          : undefined
                  }
                  className="flex-1 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm py-2 rounded-md"
                >
                  {replaceAssetsMut.isPending ? 'Saving…' : 'Save media'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
